import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ArrowLeft, Copy, Trash2 } from 'lucide-react';

import styled from 'styled-components';

import {
    GetDetailedOrderDocument,
    OrderState,
    OrderUpdateInput,
    useGetDetailedOrderQuery,
    useSoftDeleteOrderMutation,
    useUpdateOrderInfoMutation,
    TotadminPermission,
} from 'data/__generated__';

import { PAGES } from 'constants/pages';

import { GeneralInfoSection } from 'pages/Orders/OrderDetails/GeneralInfoSection';
import { StatesSection } from 'pages/Orders/OrderDetails/StatesSection';

import { Header, HeaderTitle } from 'components/Header';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { confirmPopup } from 'components/Popups/Confirm';
import { PageTitle } from 'components/PageTitle';
import { Loader } from 'components/Loader';
import { AuditLogsSection } from 'components/AuditLogs/AuditLogsSection';
import { PricesSection } from './PricesSection';
import { ProductsSection } from './ProductsSection';
import { DuplicateOrderPopup } from './DuplicateOrderPopup';
import { RecurringOrdersHistorySection } from './RecurringOrdersHistorySection';

export const OrderDetails = () => {
    const { orderId = '' }: { orderId?: string } = useParams<'orderId'>();
    const navigate = useNavigate();
    const methods = useForm<OrderUpdateInput>();

    const [updateOrderInfo, { loading: updateLoading }] = useUpdateOrderInfoMutation({
        refetchQueries: [GetDetailedOrderDocument],
    });

    const [softDeleteOrder] = useSoftDeleteOrderMutation();

    const [isDuplicateOrderPopupOpen, setIsDuplicateOrderPopupOpen] = useState(false);

    const {
        data: orderData,
        loading: orderLoading,
        error: orderError,
    } = useGetDetailedOrderQuery({
        variables: {
            orderId,
        },
    });

    if (orderLoading) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (orderError || !orderData) {
        throw new Error('Une erreur est survenue lors de la récupération de la commande');
    }

    const { detailedOrder } = orderData;

    if (!detailedOrder) {
        throw new Error(`Aucun produit avec l'id ${orderId} n'a été trouvé`);
    }

    const {
        formState: { isDirty },
        handleSubmit,
        reset,
    } = methods;

    const handleFormSubmit = handleSubmit(async (fields) => {
        if (fields.state === OrderState.Validated && detailedOrder.state !== OrderState.Validated) {
            const hasConfirmed = await confirmPopup({
                title: 'Valider la commande',
                message:
                    'La validation applique le même traitement que depuis la webapp : un email de confirmation part au client, les réservations de stock sont créées et le prix de livraison est recalculé au barème. Continuer ?',
            });

            if (!hasConfirmed) {
                return;
            }
        }

        const { data } = await updateOrderInfo({
            variables: {
                orderId,
                fieldsToUpdate: fields,
            },
        });

        if (data) {
            const {
                updateOrderInfo: { success, errors },
            } = data;
            if (success) {
                toast.success(`La commande "${orderId}" a bien été modifiée`);
                reset(fields);
            } else {
                if (errors) {
                    errors.forEach((error) => {
                        toast.error(error);
                    });
                } else {
                    throw Error("Une erreur inconnue s'est produite");
                }
            }
        } else {
            throw Error("Une erreur inconnue s'est produite");
        }
    });

    const handleDeleteOrder = async () => {
        const hasConfirmed = await confirmPopup({
            title: 'Supprimer la commande',
            message:
                'Êtes-vous sûr.e de vouloir supprimer cette commande ? Elle passera à l’état "Supprimée" et disparaîtra des vues actives. Impossible si sa livraison a déjà débuté.',
        });

        if (!hasConfirmed) {
            return;
        }

        const { data } = await softDeleteOrder({ variables: { orderId } });

        if (data?.softDeleteOrder.success) {
            toast.success('La commande a bien été supprimée');
            navigate('/orders');
            return;
        }

        const errors = data?.softDeleteOrder.errors;
        if (errors?.length) {
            errors.forEach((error) => toast.error(error));
        } else {
            toast.error("Une erreur inconnue s'est produite lors de la suppression");
        }
    };

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Commande',
            items: [
                {
                    label: 'Dupliquer',
                    icon: <Copy />,
                    onClick: () => setIsDuplicateOrderPopupOpen(true),
                    requiredPermissions: [TotadminPermission.OrdersManage],
                },
                {
                    label: 'Supprimer',
                    icon: <Trash2 />,
                    onClick: handleDeleteOrder,
                    requiredPermissions: [TotadminPermission.OrdersManage],
                },
            ],
        },
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: '/orders' }],
        },
    ];

    return (
        <Container>
            <FormProvider {...methods}>
                <Form onSubmit={handleFormSubmit}>
                    <Header>
                        <HeaderTitle>
                            <PageTitle page={PAGES.orderDetails} />
                        </HeaderTitle>
                        <CTAsContainer>
                            <ActionsAndSave
                                actionGroups={actionGroups}
                                onSave={handleFormSubmit}
                                saveDisabled={!isDirty}
                                saveLoading={updateLoading}
                                saveTitle="Mettre à jour"
                                saveRequiredPermissions={[TotadminPermission.OrdersManage]}
                            />
                        </CTAsContainer>
                    </Header>
                    <Content>
                        <ScrollableContent>
                            <GeneralInfoSection order={detailedOrder} />
                            <PricesSection order={detailedOrder} />
                            <StatesSection order={detailedOrder} />
                            <ProductsSection order={detailedOrder} />
                            {!detailedOrder.isPunctual ? <RecurringOrdersHistorySection orderId={orderId} /> : null}
                            <AuditLogsSection collectionDocumentIds={[orderId]} />
                        </ScrollableContent>
                    </Content>
                </Form>
            </FormProvider>
            <DuplicateOrderPopup
                orderId={orderId}
                isOpen={isDuplicateOrderPopupOpen}
                setIsOpen={(arg) => setIsDuplicateOrderPopupOpen(arg)}
                destinationTotemId={detailedOrder.totem._id}
                initialFormValues={{
                    deliveryTimeWindowStart: detailedOrder.deliveryTimeWindows[0]?.start ?? '08:00',
                    deliveryTimeWindowStop: detailedOrder.deliveryTimeWindows[0]?.stop ?? '11:00',
                    isOrderPunctual: true,
                }}
            />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: ${({ theme }) => theme.backgroundColor};
    color: ${({ theme }) => theme.textColor};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;

const CTAsContainer = styled.div`
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.textColor};

    & > :not(:first-child) {
        margin-left: 5px;
    }
`;
